import React from 'react'
import { useFlag } from '@sketch/modules-common'

import { Box, Flex, Text, Tooltip, UnstyledList } from '@sketch/components'
import {
  StyledDocumentCardIcons,
  StyledIcon,
  PlaceholderDSLogo,
  DSLogo,
} from './DocumentCardIcons.styles'
import { DocumentSharingStatus } from 'modules/shares/utils/getDocumentSharingStatus'

import { ReactComponent as SymbolIcon } from '@sketch/icons/link-library-16'
import { ReactComponent as TwoPersonsIcon } from '@sketch/icons/persons-two-16'
import { ReactComponent as LockIcon } from '@sketch/icons/lock-16'
import { ReactComponent as GlobeIcon } from '@sketch/icons/globe-16'
import { ReactComponent as DSIcon } from '@sketch/icons/design-system-16'
import { ReactComponent as TemplateIcon } from '@sketch/icons/templates-16'
import { ReactComponent as CommunityIcon } from '@sketch/icons/community-16'
import { ShareType } from 'modules/shares/components/EditInSketchButton'
export interface DesignSystemForLibraryList {
  name: string
  preview: string | null
}

interface IconWithTooltipProps {
  children: React.ReactNode
  content: React.ReactNode
}

const IconTooltip = ({ children, content }: IconWithTooltipProps) => {
  return (
    <Tooltip placement="bottom-start" content={content}>
      {children}
    </Tooltip>
  )
}

const sharedStatusToIconMap: Record<DocumentSharingStatus, React.ReactNode> = {
  ANYONE_WITH_LINK: (
    <IconTooltip content={<span>Anyone with the link can access</span>}>
      <StyledIcon as={GlobeIcon} aria-label="Publicly shared icon" />
    </IconTooltip>
  ),
  GUESTS: (
    <IconTooltip content={<span>Selected Guests can access</span>}>
      <StyledIcon as={TwoPersonsIcon} aria-label="Guests icon" />
    </IconTooltip>
  ),
  SELECTED_WORKSPACE_MEMBERS: (
    <IconTooltip
      content={<span>Selected people in your Workspace can access</span>}
    >
      <StyledIcon as={LockIcon} aria-label="Lock icon" />
    </IconTooltip>
  ),
  ONLY_YOU: (
    <IconTooltip content={<span>Only you can access</span>}>
      <StyledIcon as={LockIcon} aria-label="Lock icon" />
    </IconTooltip>
  ),
  WORKSPACE_MEMBERS: null,
  PUBLICLY_VISIBLE: (
    <IconTooltip content={<span>Publicly available</span>}>
      <StyledIcon as={CommunityIcon} aria-label="Publicly shared icon" />
    </IconTooltip>
  ),
}

const DesignSystemsIcon = ({
  designSystems,
}: {
  designSystems: DesignSystemForLibraryList[]
}) => {
  if (!designSystems.length) {
    return null
  }

  return (
    <>
      <Text fontWeight={600} mb={1}>
        Part of
      </Text>
      <Box mt={2}>
        <UnstyledList>
          {designSystems.map(designSystem => (
            <li key={designSystem.name}>
              <Flex mt={1}>
                <Box width={16} mr={2}>
                  {designSystem.preview ? (
                    <DSLogo src={designSystem.preview} />
                  ) : (
                    <PlaceholderDSLogo />
                  )}
                </Box>
                <Text color="foreground.primary.B">{designSystem.name}</Text>
              </Flex>
            </li>
          ))}
        </UnstyledList>
      </Box>
    </>
  )
}

export interface DocumentCardIconsProps {
  documentSharingStatus: DocumentSharingStatus
  designSystems?: DesignSystemForLibraryList[]
  type?: ShareType
}

const DocumentCardIcons = ({
  documentSharingStatus,
  designSystems,
  type,
}: DocumentCardIconsProps) => {
  const isDesignSystemsOn = useFlag('design-systems')
  const isLibrary = type === 'LIBRARY'
  const isTemplate = type === 'TEMPLATE'

  if (
    !isLibrary &&
    !isTemplate &&
    documentSharingStatus === 'WORKSPACE_MEMBERS'
  ) {
    // No icons to show
    return null
  }

  const sharingStatusIcon = sharedStatusToIconMap[documentSharingStatus]
  const showDesignSystems = !!(isDesignSystemsOn && designSystems?.length)

  return (
    <StyledDocumentCardIcons data-testid="document-card-icon-container">
      {sharingStatusIcon}
      {isLibrary && (
        <>
          <IconTooltip content={<span>Library</span>}>
            <StyledIcon as={SymbolIcon} aria-label="Library icon" />
          </IconTooltip>
          {showDesignSystems && (
            <IconTooltip
              content={
                <DesignSystemsIcon designSystems={designSystems ?? []} />
              }
            >
              <StyledIcon as={DSIcon} aria-label="Design System icon" />
            </IconTooltip>
          )}
        </>
      )}
      {isTemplate && (
        <>
          <IconTooltip content={<span>Template</span>}>
            <StyledIcon as={TemplateIcon} aria-label="Template icon" />
          </IconTooltip>
        </>
      )}
    </StyledDocumentCardIcons>
  )
}

export default DocumentCardIcons
